import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import '../styles/AccessCodeModal.css';

const MAX_LENGTH = 64;

export default function AccessCodeModal({
  open = false,
  busy = false,
  locked = false,
  attempts = 0,
  maxAttempts = 3,
  error = '',
  title = 'Enter Access Code',
  description = 'This survey requires an access code. Enter it below to continue.',
  onSubmit,
  onCancel,
}) {
  const [code, setCode] = useState('');
  const [emptyError, setEmptyError] = useState('');

  // Ensure a portal root exists
  const modalRootId = 'access-code-modal-root';
  let modalRoot = typeof document !== 'undefined' ? document.getElementById(modalRootId) : null;
  if (typeof document !== 'undefined' && !modalRoot) {
    modalRoot = document.createElement('div');
    modalRoot.id = modalRootId;
    document.body.appendChild(modalRoot);
  }

  // Reset input when modal opens
  useEffect(() => {
    if (open) {
      setCode('');
      setEmptyError('');
    }
  }, [open]);

  // Lock scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const attemptsRemaining = useMemo(() => Math.max(maxAttempts - attempts, 0), [attempts, maxAttempts]);

  if (!open || !modalRoot) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy || locked) return;
    const trimmed = code.trim();
    if (!trimmed) {
      setEmptyError('Please enter the access code.');
      return;
    }
    setEmptyError('');
    const ok = await onSubmit?.(trimmed);
    if (ok) {
      setCode('');
    }
  };

  const mergedError = emptyError || error || '';

  const modal = (
    <div className="code-overlay" role="dialog" aria-modal="true" aria-labelledby="access-code-modal-title">
      <div className="code-modal">
        <h2 id="access-code-modal-title" className="code-title">{title}</h2>
        <p className="code-text">{description}</p>

        {mergedError && (
          <div className={`code-error${locked ? ' code-error--locked' : ''}`}>{mergedError}</div>
        )}

        {!locked && (
          <form onSubmit={handleSubmit} className="code-form">
            <input
              className="code-input"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.slice(0, MAX_LENGTH))}
              placeholder="Access code"
              autoFocus
              disabled={busy}
            />
            <button className="code-btn code-btn--submit" type="submit" disabled={busy || !code.trim()}>
              {busy ? 'Checking…' : 'Submit code'}
            </button>
          </form>
        )}

        {locked && (
          <div className="code-locked-note">Access has been temporarily blocked after too many incorrect attempts.</div>
        )}

        {!locked && attempts > 0 && attemptsRemaining > 0 && (
          <div className="code-hint">Attempts remaining: {attemptsRemaining}</div>
        )}

        <div className="code-actions">
          {onCancel && !locked && (
            <button
              type="button"
              className="code-btn code-btn--ghost"
              onClick={() => onCancel?.()}
              disabled={busy}
            >
              Cancel
            </button>
          )}
          {locked && onCancel && (
            <button
              type="button"
              className="code-btn code-btn--submit"
              onClick={() => onCancel?.()}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, modalRoot);
}
